import React, { useState } from 'react';
import { Box, Button, Chip, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faClock, faTimes, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { TriageActions, TriageSnoozeDays } from './DrawerFacetTypes';

const SNOOZE_OPTIONS: Array<{ days: TriageSnoozeDays; label: string; testId: string }> = [
    { days: 1, label: 'Snooze 1 day', testId: 'WB_TRIAGE_ACTION_SNOOZE_1D' },
    { days: 3, label: 'Snooze 3 days', testId: 'WB_TRIAGE_ACTION_SNOOZE_3D' },
    { days: 7, label: 'Snooze 1 week', testId: 'WB_TRIAGE_ACTION_SNOOZE_7D' },
];

const verbSx = {
    textTransform: 'none' as const,
    fontWeight: 700,
    fontSize: '0.8rem',
    minHeight: 44,
    px: 1.5,
};

const classifyChipSx = (active: boolean) => ({
    height: 44,
    fontWeight: 700,
    fontSize: '0.75rem',
    cursor: 'pointer',
    color: active ? 'var(--color-on-accent)' : 'var(--color-text-secondary)',
    bgcolor: active ? 'var(--color-accent)' : 'transparent',
    border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-border)'}`,
    '&:hover': active
        ? { bgcolor: 'var(--color-accent-hover)', color: 'var(--color-on-accent-hover)' }
        : { bgcolor: 'rgba(var(--color-accent-rgb), 0.08)' },
    '&:focus-visible': { outline: '2px solid var(--color-accent)', outlineOffset: '2px' },
});

interface DrawerTriageActionRowProps {
    actions: TriageActions;
    mode?: 'actions' | 'classification' | 'all';
    /** Sticky footer: teal Start, outlined Snooze and Dismiss. */
    layout?: 'inline' | 'bar';
}

/**
 * The triage decision, as buttons, where the eye lands first. Replaces the header
 * captions that used to explain where the actions were hidden.
 */
const DrawerTriageActionRow: React.FC<DrawerTriageActionRowProps> = ({ actions, mode = 'all', layout = 'inline' }) => {
    const bar = layout === 'bar';
    const [snoozeAnchor, setSnoozeAnchor] = useState<null | HTMLElement>(null);
    const snoozeOpen = Boolean(snoozeAnchor);
    const type = (actions.currentType || '').toUpperCase();

    const pickSnooze = (days: TriageSnoozeDays) => {
        setSnoozeAnchor(null);
        actions.onSnooze(days);
    };

    return (
        <Box
            {...(mode === 'classification' ? {} : { 'data-testid': 'WB_TRIAGE_ACTION_ROW' })}
            role="group"
            aria-label={mode === 'classification' ? 'Item classification' : 'Triage actions'}
            sx={bar ? {
                display: 'grid',
                gridTemplateColumns: '1.15fr 1fr 1fr',
                gap: 1,
                p: 1.5,
                borderTop: '1px solid var(--color-border)',
                bgcolor: 'var(--color-bg-secondary)',
                // The drawer body is one scrollport (#316). The verbs stay above the
                // source-home accordion (#310), so pin both edges: they hold at the
                // bottom while content is below them and at the top once scrolled past.
                position: 'sticky',
                top: 0,
                bottom: 0,
                zIndex: 1,
            } : {
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                borderRadius: 2,
                border: '1px solid var(--color-border)',
                bgcolor: 'var(--color-bg-card)',
            }}
        >
            {mode !== 'classification' && <>
            <Tooltip title="This is yours now">
                <Box component="span" sx={bar ? { display: 'block', minWidth: 0 } : undefined}>
            <Button
                variant="contained"
                size="small"
                color="inherit"
                fullWidth={bar}
                disabled={actions.disabled}
                onClick={actions.onStart}
                data-testid="WB_TRIAGE_ACTION_START"
                startIcon={<FontAwesomeIcon icon={faPlay} style={{ fontSize: '0.7rem' }} />}
                sx={{
                    ...verbSx,
                    bgcolor: 'var(--color-accent)',
                    color: 'var(--color-on-accent)',
                    boxShadow: 'var(--shadow-accent)',
                    '&:hover': {
                        bgcolor: 'var(--color-accent-hover)',
                        color: 'var(--color-on-accent-hover)',
                    },
                    '&.Mui-disabled': { bgcolor: 'var(--color-accent-muted)', color: 'var(--color-text-muted)' },
                }}
            >
                Start
            </Button>
                </Box>
            </Tooltip>
            <Tooltip title="Bring it back later">
                <Box component="span" sx={bar ? { display: 'block', minWidth: 0 } : undefined}>
            <Button
                variant="outlined"
                size="small"
                color="inherit"
                fullWidth={bar}
                disabled={actions.disabled}
                onClick={(event) => setSnoozeAnchor(event.currentTarget)}
                data-testid="WB_TRIAGE_ACTION_SNOOZE"
                aria-haspopup="menu"
                aria-expanded={snoozeOpen}
                startIcon={<FontAwesomeIcon icon={faClock} style={{ fontSize: '0.7rem' }} />}
                endIcon={<FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '0.6rem' }} />}
                sx={{ ...verbSx, color: 'var(--color-text-primary)', borderColor: 'var(--color-border)', bgcolor: 'var(--color-bg-card)' }}
            >
                Snooze
            </Button>
                </Box>
            </Tooltip>
            <Menu
                anchorEl={snoozeAnchor}
                open={snoozeOpen}
                onClose={() => setSnoozeAnchor(null)}
                MenuListProps={{ dense: true }}
                sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
            >
                {SNOOZE_OPTIONS.map((option) => (
                    <MenuItem key={option.days} onClick={() => pickSnooze(option.days)} data-testid={option.testId}>
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>
            <Tooltip title="Not work. Let it go.">
                <Box component="span" sx={bar ? { display: 'block', minWidth: 0 } : undefined}>
            <Button
                variant={bar ? 'outlined' : 'text'}
                size="small"
                color="inherit"
                fullWidth={bar}
                disabled={actions.disabled}
                onClick={actions.onDismiss}
                data-testid="WB_TRIAGE_ACTION_DISMISS"
                startIcon={<FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.7rem' }} />}
                sx={{
                    ...verbSx,
                    color: 'var(--color-text-secondary)',
                    ...(bar ? { borderColor: 'var(--color-border)', bgcolor: 'var(--color-bg-card)' } : {}),
                }}
            >
                Dismiss
            </Button>
                </Box>
            </Tooltip>

            </>}
            {mode !== 'actions' && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: { xs: 0, sm: 'auto' }, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>
                    This is
                </Typography>
                <Chip
                    label="Application"
                    size="small"
                    clickable
                    disabled={actions.disabled}
                    onClick={actions.onClassifyApplication}
                    data-testid="WB_TRIAGE_CLASSIFY_APPLICATION"
                    aria-pressed={type === 'APPLICATION'}
                    sx={classifyChipSx(type === 'APPLICATION')}
                />
                <Chip
                    label="Task"
                    size="small"
                    clickable
                    disabled={actions.disabled}
                    onClick={actions.onClassifyTask}
                    data-testid="WB_TRIAGE_CLASSIFY_TASK"
                    aria-pressed={type === 'TASK'}
                    sx={classifyChipSx(type === 'TASK')}
                />
                {actions.onIgnoreSource && (
                    <Button
                        variant="text"
                        size="small"
                        disabled={actions.disabled}
                        onClick={actions.onIgnoreSource}
                        data-testid="WB_TRIAGE_IGNORE_SOURCE"
                        sx={{ ...verbSx, fontWeight: 600, color: 'var(--color-text-muted)' }}
                    >
                        Not a task — ignore source
                    </Button>
                )}
            </Box>}
        </Box>
    );
};

export default DrawerTriageActionRow;
